import React from 'react';
import { Stack } from 'react-bootstrap';
import { FormatFieldValue } from '../../components/FormatFieldValue';
import {
  SalaryChangeOverview,
  SalaryChangeData,
} from '../../../../../components/RequestForms/RequestForm/components/salaryChange';
import {
  PositionChangeOverview,
  PositionChangeData,
} from '../../../../../components/RequestForms/RequestForm/components/positionChange';
import {
  ChangeRequestDetailsFieldsFragment,
  EmployeeDataChangeRequestObjectAction,
  useEmployeeDataChangeRequestByIdQuery,
} from '../../../../../__generated__/graphql';
import ServerError from '../../../../../../../components/ServerError';
import SkeletonCard from '../../../../../components/Skeleton/Card';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';

type ChangeRequestDetailsProps = {
  changeRequest: {
    id: string;
    employeeId: string;
  };
};

export const ChangeRequestDetails: React.FC<ChangeRequestDetailsProps> = ({
  changeRequest,
}) => {
  const t = useNamespacedTranslation('changeRequests.previewModal');
  const { data, loading, error } = useEmployeeDataChangeRequestByIdQuery({
    variables: { id: changeRequest.id },
  });

  const { salaryData, positionData } = useSalaryAndPositionData(
    data?.employeeDataChangeRequest ?? undefined,
  );

  if (loading) {
    return (
      <Stack gap={4}>
        <SkeletonCard height={100} />
        <SkeletonCard height={100} />
        <SkeletonCard height={100} />
      </Stack>
    );
  }

  const request = data?.employeeDataChangeRequest;

  if (!request || error) {
    return <ServerError />;
  }

  const employeeId = request.flair__Employee__c;

  const objectChanges = request.objectChanges;

  const isCreation =
    request.flair__Object_Action__c ===
    EmployeeDataChangeRequestObjectAction.ObjectCreate;
  const headerText = isCreation
    ? t('objectCreation', { objectLabel: objectChanges.objectLabel })
    : t('objectChanges', { objectLabel: objectChanges.objectLabel });

  return (
    <div className="fields-container">
      <div className="fw-bold mb-3">{headerText}</div>
      <Stack gap={3}>
        {positionData && (
          <PositionChangeOverview positionChange={positionData} />
        )}
        {salaryData && employeeId && (
          <SalaryChangeOverview
            salaryChange={salaryData}
            employeeId={employeeId}
          />
        )}

        {objectChanges.fields
          .filter((field) => {
            return !(
              field.fieldApiName === 'RecordTypeName' &&
              field.newValue === 'Master'
            );
          })
          .map((field) => {
            const newValue = field.newDisplayName || field.newValue;
            const oldValue = field.oldDisplayName || field.oldValue;

            return (
              <div
                key={field.fieldApiName}
                className="bg-light rounded p-4 mb-2">
                <div className="fw-bold mb-2">{field.fieldLabel}</div>
                <div className="text-break">
                  <FormatFieldValue
                    value={newValue ?? null}
                    type={field.fieldType}
                  />
                </div>
                {!isCreation && (
                  <div className="text-muted text-decoration-line-through text-break">
                    <FormatFieldValue
                      value={oldValue ?? null}
                      type={field.fieldType}
                    />
                  </div>
                )}
              </div>
            );
          })}
      </Stack>
    </div>
  );
};

const useSalaryAndPositionData = (
  request: ChangeRequestDetailsFieldsFragment | undefined,
) => {
  if (!request) {
    return { salaryData: undefined, positionData: undefined };
  }
  const changesNewValues = getChangeRequestNewValues(request);
  const salaryData = changesNewValues.salaryChange;
  const positionData = changesNewValues.positionChange;
  return { salaryData, positionData };
};

type EmployeeFieldChanges = {
  salaryChange?: SalaryChangeData;
  positionChange?: PositionChangeData;
};

const getChangeRequestNewValues = (
  request: ChangeRequestDetailsFieldsFragment,
): EmployeeFieldChanges => {
  return request.flair__Changes__c ? JSON.parse(request.flair__Changes__c) : {};
};
